import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { apiClient } from "../apiClient";

const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

const AuthContext = createContext(null);

const getBackendMessage = (data) =>
  typeof data?.message === "string" ? data.message : "";

const isBackendSuccess = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

function normalizeValidateResponse(data) {
  const authenticated =
    data?.authenticated ?? data?.isAuthenticated ?? data?.auth ?? undefined;

  const isAuthenticated =
    typeof authenticated === "boolean" ? authenticated : Boolean(data?.user);

  const user = data?.user ?? (isAuthenticated ? data : null);

  return { isAuthenticated, user };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshTimerRef = useRef(null);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const startRefreshTimer = useCallback(() => {
    stopRefreshTimer();

    refreshTimerRef.current = setInterval(async () => {
      try {
        const ok = await apiClient.refreshSession();
        if (!ok) {
          setUser(null);
          stopRefreshTimer();
        }
      } catch {
        setUser(null);
        stopRefreshTimer();
      }
    }, REFRESH_INTERVAL_MS);
  }, [stopRefreshTimer]);

  const validate = useCallback(async () => {
    const data = await apiClient.request("/api/validate", {
      method: "GET",
    });

    return normalizeValidateResponse(data);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const result = await validate();
        if (cancelled) return;
        setUser(result.isAuthenticated ? result.user : null);
      } catch {
        if (cancelled) return;
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      stopRefreshTimer();
    };
  }, [stopRefreshTimer, validate]);

  const isAuthenticated = Boolean(user);

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated) {
      startRefreshTimer();
    } else {
      stopRefreshTimer();
    }
  }, [isAuthenticated, loading, startRefreshTimer, stopRefreshTimer]);

  /* ===============================
     NORMAL LOGIN (UNCHANGED)
  ================================*/
  const login = useCallback(
    async (loginValue, password) => {
      const data = await apiClient.request("/api/login", {
        method: "POST",
        body: { login: loginValue, password },
        _retryAfterRefresh: false,
      });

      if (data && typeof data === "object" && "success" in data) {
        const message = getBackendMessage(data);
        if (!isBackendSuccess(data.success)) {
          const error = new Error(message);
          error.serverMessage = message;
          error.responseData = data;
          throw error;
        }
      }

      const result = await validate();
      setUser(result.isAuthenticated ? result.user : null);
      return { ...result, ...data };
    },
    [validate],
  );

  /* ===============================
     🔹 STUDENT AUTO LOGIN (ADDED)
  ================================*/
  const loginWithToken = useCallback(
    async (token) => {
      const data = await apiClient.request("/api/student-login", {
        method: "POST",
        body: { token },
        _retryAfterRefresh: false,
      });

      if (data && typeof data === "object" && "success" in data) {
        const message = getBackendMessage(data);
        if (!isBackendSuccess(data.success)) {
          throw new Error(message || "Student login failed");
        }
      }

      const result = await validate();
      setUser(result.isAuthenticated ? result.user : null);
      return result;
    },
    [validate],
  );

  const logout = useCallback(async () => {
    stopRefreshTimer();

    try {
      await apiClient.request("/api/logout", {
        method: "POST",
        _retryAfterRefresh: false,
      });
    } catch {
      // ignore
    } finally {
      setUser(null);
    }
  }, [stopRefreshTimer]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated,
      login,
      loginWithToken, // 🔹 EXPOSED
      logout,
    }),
    [isAuthenticated, loading, login, loginWithToken, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
